import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookmarkCheck, ArrowRight, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SaveButton from "./SaveButton";
import VoiceButton from "./VoiceButton";

/**
 * SavedShelf — "Your Notebook" strip.
 * Fetches the caller's saved Library chapters and renders them as a
 * horizontal scrollable row of compact cards.  Silent hide when empty
 * or logged out (so it never becomes noise on new accounts).
 *
 * Props:
 *   variant  — "compact" (default, dashboard) | "full" (library homepage)
 *   limit    — max chapters to show (default 8)
 *   className — extra classes on the outer <section>
 */
export default function SavedShelf({ variant = "compact", limit = 8, className = "" }) {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [topBody, setTopBody] = useState(null);      // narration text for the first card
  const topSlug = rows && rows[0] ? rows[0].slug : null;

  useEffect(() => {
    if (!user) { setRows([]); return; }
    let cancelled = false;
    api.get("/library/saved/me")
      .then(({ data }) => {
        if (!cancelled) setRows(Array.isArray(data.articles) ? data.articles : []);
      })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, [user]);

  // Fetch just enough of the top saved chapter to narrate a warm opener.
  useEffect(() => {
    if (!topSlug) { setTopBody(null); return; }
    let cancelled = false;
    setTopBody(null);
    api.get(`/library/${topSlug}`)
      .then(({ data }) => {
        if (cancelled) return;
        const raw = (data.body_md || "").replace(/[#*`>_[\]()]/g, "").replace(/\s+/g, " ").trim();
        setTopBody(raw.slice(0, 1200));
      })
      .catch(() => { if (!cancelled) setTopBody(""); });
    return () => { cancelled = true; };
  }, [topSlug]);

  if (!user) return null;
  if (rows === null) return null;    // still loading — avoid flicker
  if (rows.length === 0) return null; // silent hide when notebook is empty

  const shown = rows.slice(0, limit);

  return (
    <section
      className={`rounded-3xl border-2 border-royal/70 bg-parchment/70 shadow-[8px_8px_0_0_#2E1A47] overflow-hidden ${className}`}
      data-testid="saved-shelf"
    >
      <header className="px-6 pt-5 pb-3 border-b border-royal/15 flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-gold/25 text-royal flex items-center justify-center flex-shrink-0">
            <BookmarkCheck className="w-5 h-5 fill-gold" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.28em] font-bold text-gold mb-0.5">
              Your notebook
            </div>
            <h3 className={`font-heading font-semibold text-royal leading-tight ${variant === "full" ? "text-2xl" : "text-xl"}`}>
              {rows.length === 1 ? "One saved chapter, waiting for you." : `${rows.length} saved chapters, waiting for you.`}
            </h3>
          </div>
        </div>
        {rows.length > limit && (
          <Link to="/library" className="text-xs text-royal hover:text-marigold font-semibold inline-flex items-center gap-1" data-testid="saved-shelf-see-all">
            {rows.length - limit} more · scroll <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </header>

      <div className="px-6 py-4 overflow-x-auto">
        <ul className="flex gap-3 min-w-max">
          {shown.map((a, i) => (
            <li key={a.slug} className={`w-[240px] flex-shrink-0 ${i === 0 ? "relative pt-4 pb-3" : ""}`}>
              {i === 0 && (
                <div
                  className="absolute -top-1 left-2 right-2 flex items-start gap-1 z-10 pointer-events-none"
                  data-testid="saved-shelf-pradhan-whisper"
                >
                  <span className="font-hand text-marigold text-[13px] leading-tight -rotate-2 select-none whitespace-normal">
                    Ms. Pradhan says start with this one ↓
                  </span>
                </div>
              )}
              {i === 0 && topBody && (
                <div className="absolute -bottom-1 left-3 z-10" data-testid="saved-shelf-voice-pick">
                  <div className="bg-royal text-parchment border-2 border-royal rounded-full shadow-[3px_3px_0_0_#C9A24A] pl-3 pr-1 py-0.5 flex items-center gap-1.5">
                    <span className="font-hand text-[11px] text-gold2 leading-none whitespace-nowrap">listen while you sip</span>
                    <div className="[&_button]:!text-parchment [&_button:hover]:!bg-parchment/10">
                      <VoiceButton text={topBody} label="" data-testid="saved-shelf-voice-btn" />
                    </div>
                  </div>
                </div>
              )}
              <div className="group relative rounded-2xl border-2 border-royal/30 bg-cream hover:border-royal hover:-translate-y-0.5 shadow-[3px_3px_0_0_rgba(46,26,71,0.9)] hover:shadow-[5px_5px_0_0_#2E1A47] transition-all h-full">
                <Link
                  to={`/library/${a.slug}`}
                  className="block p-3.5 h-full"
                  data-testid={`saved-shelf-card-${a.code || a.slug}`}
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[9px] font-mono font-bold text-royal/70">
                      {a.book_code}
                    </span>
                    {a.module_code && (
                      <>
                        <span className="text-royal/30 text-[9px]">·</span>
                        <span className="text-[9px] font-mono font-bold text-royal/70">
                          {a.module_code}·A{String(a.article_num || 0).padStart(2, "0")}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="font-heading text-sm font-semibold text-royal leading-tight line-clamp-3 group-hover:text-marigold transition-colors pr-6">
                    {a.title}
                  </div>
                  {a.summary && variant === "full" && (
                    <div className="text-[11px] text-ink2 mt-1.5 line-clamp-2 leading-relaxed">
                      {a.summary}
                    </div>
                  )}
                </Link>
                {/* Unsave in the corner — sits above the link */}
                <div className="absolute top-2 right-2">
                  <SaveButton
                    slug={a.slug}
                    title={a.title}
                    size="sm"
                    initialSaved
                    onChange={(saved) => {
                      if (!saved) setRows((r) => (r || []).filter((x) => x.slug !== a.slug));
                    }}
                    testId={`saved-shelf-unsave-${a.code || a.slug}`}
                  />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
